import {useCallback} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import useGenerateAppDownloadUrl from 'hooks/useGenerateAppDownloadUrl'
import {useIsRoomOfferAppLocked} from 'hooks/useIsRoomOfferAppLocked'
import {getOfferContextFromRoomContext} from 'middleware/analytics/contexts/roomContext'
import {RoomOfferUrlError} from 'modules/accommodation/errors'
import {getRoomsActiveFilters} from 'modules/accommodation/selectors'
import {getCheckoutLink} from 'modules/accommodation/utils'
import {getCurrencyCode} from 'modules/meta/selectors'
import {getRoomDetailOverlayVisibility} from 'modules/overlay/selectors'
import {getIsLockedDealExcludingGHA} from 'modules/search/selectors'
import {getCommonPaymentPageLandingParams} from 'modules/searchApi/selectors'
import {toggle} from 'opticks'

import {getAnonymousId} from '@daedalus/core/src/_web/anonymousId'
import {trackEvent} from '@daedalus/core/src/analytics/modules/actions'
import {
  Action,
  AnalyticsContext,
  Category,
  Entity,
  Page,
  RoomContext,
  Team
} from '@daedalus/core/src/analytics/types/Events'
import {
  AuthenticationFlows,
  SourceComponentType
} from '@daedalus/core/src/auth/types/Auth'
import {getLinkByType} from '@daedalus/core/src/offer/business/getLinkByType'
import {getOfferPerks} from '@daedalus/core/src/offer/business/getOfferPerks'
import {ActionLinkTypes} from '@daedalus/core/src/offer/types/actionLink'
import {Offer} from '@daedalus/core/src/offer/types/offer'
import {
  getDisplayPrice,
  getTotalPrice
} from '@daedalus/core/src/price/business/price'
import {roomsFilterListToMap} from '@daedalus/core/src/room/business/utils/filters'
import {BaseUrlCheckoutParams} from '@daedalus/core/src/utils/url/types/UrlParams'
import {useLogIn} from '@daedalus/shared/src/authentication/hooks/useLogIn'

import {useDefferedOfferInteraction} from './useDeferredOfferInteraction'
const getEventEntitiy = ({
  isRoomOfferAppLocked,
  isLockedDeal
}: {
  isRoomOfferAppLocked: boolean
  isLockedDeal: boolean
}) => {
  if (isRoomOfferAppLocked) return Entity.AppExclusiveOffer
  if (isLockedDeal) return Entity.LockedRoomOffer
  return Entity.RoomOffer
}

const captureSentryError = (offer: Offer) => {
  if (typeof Sentry !== 'undefined')
    Sentry.captureException(
      new RoomOfferUrlError(
        'Checkout link was not found in the SAPI offer',
        {}
      ),
      {extra: {offer}}
    )
}

export const useRoomOfferInteraction = ({
  hotelId,
  roomId,
  offer,
  component,
  roomContext
}: {
  hotelId: string
  roomId: string
  offer: Offer
  component: string
  roomContext: RoomContext
}) => {
  const dispatch = useDispatch()
  const isLockedDeal =
    useSelector(getIsLockedDealExcludingGHA(offer)) && !offer.isClicked
  const {isRoomOfferAppLocked, appDownloadLinkId} = useIsRoomOfferAppLocked({
    offerId: offer.id
  })

  const offerPerks = getOfferPerks(
    offer?.cancellationPenalties,
    offer?.services
  )

  const login = useLogIn()

  const anonymousId = getAnonymousId()

  const commonPaymentPageParams = useSelector(getCommonPaymentPageLandingParams)
  const offerParams = {
    roomId,
    offerId: offer.id,
    providerCode: offer.providerCode,
    providerRateType: offer.providerRateType,
    cug: offer.cug[0],
    offerPerks
  }

  const paymentPageParams = {
    ...commonPaymentPageParams,
    ...offerParams
  } as Partial<BaseUrlCheckoutParams>

  const checkoutUrl = getLinkByType(offer.links, ActionLinkTypes.CHECKOUT)

  const isRoomDetailsOverlayOpen = useSelector(getRoomDetailOverlayVisibility)
  const activeRoomsFilters = useSelector(getRoomsActiveFilters)

  const currencyCode = useSelector(getCurrencyCode)

  const {generateAppLockedDealDeepLink} = useGenerateAppDownloadUrl(Page.Search)

  const trackClickEvent = useCallback(() => {
    dispatch(
      trackEvent({
        category: Category.User,
        entity: getEventEntitiy({isLockedDeal, isRoomOfferAppLocked}),
        action: Action.Clicked,
        team: Team.Select,
        component,
        analyticsContext: {
          [AnalyticsContext.RoomContext]: roomContext,
          [AnalyticsContext.OfferContext]: getOfferContextFromRoomContext(
            roomContext,
            offer.id
          )
        },
        payload: {
          roomFilterApplied: activeRoomsFilters.length > 0,
          allSelectedRoomFilters: roomsFilterListToMap(activeRoomsFilters),
          ...(isRoomOfferAppLocked ? {bannerId: appDownloadLinkId} : {})
        },
        page: Page.Accommodation
      })
    )
  }, [
    activeRoomsFilters,
    appDownloadLinkId,
    component,
    dispatch,
    isLockedDeal,
    isRoomOfferAppLocked,
    offer.id,
    roomContext
  ])

  const {defferedOfferClick} = useDefferedOfferInteraction({
    offer: {offer}
  })

  const navigateToPP = (checkoutLandingUrl: string) => {
    if (isRoomDetailsOverlayOpen) {
      // If the user is navigating back from the PP,
      // we don't want to show the roomDetailsOverlay.
      window.location.replace(checkoutLandingUrl)
    } else {
      window.location.assign(checkoutLandingUrl)
    }
  }

  const handleClick = () => {
    if (!checkoutUrl) {
      captureSentryError(offer)
      return undefined
    }

    const checkoutLandingUrl = getCheckoutLink({
      checkoutUrl,
      paymentPageParams
    })

    trackClickEvent()

    if (isLockedDeal) {
      login({
        anonymousId,
        flow: AuthenticationFlows.UnlockOverlay,
        sourceComponent: SourceComponentType.UnlockOverlay,
        unlockHotelId: hotelId
      })
      return
    }

    if (isRoomOfferAppLocked) {
      const appDownloadUrl = generateAppLockedDealDeepLink(appDownloadLinkId, {
        hotelId,
        clickedOfferRate: getTotalPrice(getDisplayPrice(offer.prices)),
        currencyCode
      })

      return window.open(appDownloadUrl)
    }

    if (toggle('23442652-offer-check-on-ap-v1', false, true)) {
      return defferedOfferClick(checkoutLandingUrl)
    }

    navigateToPP(checkoutLandingUrl)
  }

  return {handleClick}
}
